import { cx, formatNumber } from "../utils/format";

export interface StackedBar { label: string; ai: number; people: number; }

/** Tickets per day or week, split into what the AI answered on its own (the AI hue, its output) and what people handled.
 *  Labels thin out by themselves once there are too many bars to name each one. */
export function StackedBars({ data, legend }: { data: StackedBar[]; legend: { ai: string; people: string } }) {
  const max = Math.max(...data.map((d) => d.ai + d.people), 1);
  const every = Math.ceil(data.length / 10);
  return (
    <figure className="ob-bars-figure">
      <div className="ob-bars" role="img" aria-label={`${legend.people} and ${legend.ai}, ${data.length} bars`}>
        {data.map((d, i) => (
          <div className="ob-bars__col" key={d.label} title={`${d.label}: ${d.people} ${legend.people.toLowerCase()}, ${d.ai} ${legend.ai.toLowerCase()}`}>
            <span className="ob-bars__stack" style={{ ["--v" as string]: ((d.ai + d.people) / max) * 100 }}>
              {d.ai > 0 && <span className="ob-bars__bar ob-bars__bar--ai" style={{ flexGrow: d.ai }} />}
              <span className="ob-bars__bar" style={{ flexGrow: d.people }} />
            </span>
            <span className="ob-bars__label">{(data.length - 1 - i) % every === 0 ? d.label : ""}</span>
          </div>
        ))}
      </div>
      <figcaption className="ob-legend"><span className="ob-legend__item"><span className="ob-legend__key" />{legend.people}</span><span className="ob-legend__item"><span className="ob-legend__key ob-legend__key--ai" />{legend.ai}</span></figcaption>
    </figure>
  );
}

/** One bar cut into parts that add up to a whole, with the parts named below it: what became of the drafts. */
export function ShareBar({ parts }: { parts: { id: string; label: string; value: number; tone?: "ai" | "strong" | "mid" | "soft" | "faint" }[] }) {
  const total = parts.reduce((n, p) => n + p.value, 0) || 1;
  return (
    <div className="ob-share">
      <div className="ob-share__bar" role="img" aria-label={parts.map((p) => `${p.label}: ${Math.round((p.value / total) * 100)} %`).join(", ")}>
        {parts.filter((p) => p.value > 0).map((p) => <span key={p.id} className={cx("ob-share__part", `ob-share__part--${p.tone ?? "mid"}`)} style={{ flexGrow: p.value }} />)}
      </div>
      <ul className="ob-share__list">
        {parts.map((p) => (
          <li key={p.id} className="ob-share__row">
            <span className={cx("ob-share__key", `ob-share__part--${p.tone ?? "mid"}`)} /><span className="ob-share__label">{p.label}</span>
            <span className="ob-share__value">{formatNumber(p.value)}</span><span className="ob-share__pct">{Math.round((p.value / total) * 100)} %</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** A quiet horizontal meter inside a table cell or a row: a share of the row's maximum. */
export function RowMeter({ value, tone }: { value: number; tone?: "ai" }) {
  return <span className={cx("ob-rowmeter", tone === "ai" && "ob-rowmeter--ai")} style={{ ["--v" as string]: Math.round(Math.max(0, Math.min(1, value)) * 100) }} aria-hidden="true" />;
}

export function Heatmap({ days, rows }: { days: string[]; rows: { hour: string; values: number[] }[] }) {
  const max = Math.max(...rows.flatMap((r) => r.values), 1);
  return (
    <table className="ob-heat">
      <thead><tr><th></th>{days.map((d) => <th key={d}>{d}</th>)}</tr></thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.hour}>
            <td className="ob-heat__hour">{r.hour}</td>
            {r.values.map((v, i) => <td key={i} className="ob-heat__cell" style={{ ["--h" as string]: (v / max).toFixed(2) }}>{v === 0 ? "—" : v}</td>)}
          </tr>
        ))}
      </tbody>
    </table>
  );
}
